import { useState } from "react";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { FormProvider, useForm } from "react-hook-form";

import { getGoHireRoute, getJobDetailsRoute } from "../../../../../routers/routeUtils";
import { CurrencyEnum } from "../../../../../enums/currencies.enum";

import { UserRoleEnum } from "../../../../User/enums/userRole";

import { ApplyCriteriaEnum } from "../../../enum/jobEnums";
import JobBoostStepLooked from "../../../components/JobBoost-Looked";
import { CreateJobHeader, CreateJobFooter } from "../../../components";
import { createProfessionalJobSchema } from "../../../schemas/createJobSchema";
import HiringJourneyStepLooked from "../../../components/HiringJourney-Looked";
import { CreateProfessionalJob as CreateProfessionalJobType } from "../../../types/createJobSchema";

import JobPublished from "./Steps/JobPublished";
import JobDetailsStep from "./Steps/JobDetails";
import PreviewAndPublishJob from "./Steps/PreviewAndPublishJob";
import { useCreateProfessionalJob } from "../../../api/mutations/useCreateProfessionalJob";
import { CancelButtonIcon, PrevButtonIcon } from "../../../assets/icons/go-hire-page-icons";

const CreateProfessionalJob = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [jobId, setJobId] = useState<number | null>(null);

  const methods = useForm<CreateProfessionalJobType>({
    resolver: zodResolver(createProfessionalJobSchema),
    mode: "all",
    defaultValues: {
      jobLocation: [],
      nationality: [],
      talentProfessionalLocation: [],
      salaryCurrency: CurrencyEnum.EGP,
      countriesScoutedIn: [],
      certificateAndLicenses: [],
      countriesCoachedIn: [],
      isExternal: false,
    },
  });

  const handleGoBack = () => {
    if (step === 0 || step === 4) {
      setStep(0);
      navigate(getGoHireRoute());
    } else {
      setStep((prev) => prev - 1);
    }
  };

  const { t } = useTranslation("goHire", { keyPrefix: "createJobPage" });

  const handleValidateJobDetails = async () => {
    let isValid = await methods.trigger([
      "jobName",
      "position",
      "jobMode",
      "jobLocation",
      "jobDescription",
      "applyEnds",
      "salaryAmount",
      "salaryCurrency",
      "salaryPer",
      "howManyHiresNeeded",
      "nationality",
      "talentProfessionalLocation",
      ApplyCriteriaEnum.onlyVerifiedFootballers,
      ApplyCriteriaEnum.onlyWithTestimonials,
      ApplyCriteriaEnum.profileCompletionAbove80,
      "languagesSpoken",
      "willingnessToRelocate",
    ]);

    if (!isValid) return false;

    const position = methods.getValues("position");

    if (position === UserRoleEnum.COACH) {
      if (methods.getValues("isCountriesCoachedInActive") && !(await methods.trigger(["countriesCoachedIn"]))) {
        return false;
      }

      if (methods.getValues("isCertificateAndLicensesActive") && !(await methods.trigger(["certificateAndLicenses"]))) {
        return false;
      }
    }

    if (
      position === UserRoleEnum.SCOUT &&
      methods.getValues("isCountriesScoutedInActive") &&
      !(await methods.trigger(["countriesScoutedIn"]))
    ) {
      return false;
    }

    if (position === UserRoleEnum.AGENT && !(await methods.trigger(["totalCareerTransfers"]))) {
      return false;
    }

    if (methods.getValues("isClubsWorkedInActive") && !(await methods.trigger(["clubsWorkedIn"]))) {
      return false;
    }

    if (methods.getValues("isLanguagesSpokenActive") && !(await methods.trigger(["languagesSpoken"]))) {
      return false;
    }

    if (methods.getValues("isIntroductoryVideoActive") && !(await methods.trigger(["introductoryVideo"]))) {
      return false;
    }

    if (position === UserRoleEnum.OTHER && !(await methods.trigger(["subRole"]))) {
      return false;
    }

    return true;
  };

  const handleGoToNextStep = async () => {
    const isValid = await handleValidateJobDetails();
    if (!isValid) return;

    if (step === 3) {
      const isFormValid = await methods.trigger();
      if (!isFormValid) return;
      methods.handleSubmit(handleCreateJobProfessional)();
      return;
    }

    if (step === 4) return;
    setStep((prevStep) => prevStep + 1);
  };

  const jobDetails = methods.getValues();
  const createJobStepsMapper: Record<number, JSX.Element> = {
    0: <JobDetailsStep key="job-details" />,
    1: <HiringJourneyStepLooked key="hiring-journey" />,
    2: <JobBoostStepLooked key="job-boost" />,
    3: <PreviewAndPublishJob key="preview-publish" jobDetails={jobDetails} />,
    4: <JobPublished key="job-published" />,
  };

  const { mutate: createJobProfessional, isPending } = useCreateProfessionalJob({
    onSuccess: (data) => {
      setStep(4);
      setJobId(data.id);
    },
  });

  const handleCreateJobProfessional = (data: CreateProfessionalJobType) => {
    createJobProfessional(data);
  };

  const handlePreviewClick = async () => {
    const isValid = await handleValidateJobDetails();
    if (!isValid) return;
    setStep(3);
  };

  const BackButtonIcon = step !== 0 ? <PrevButtonIcon /> : <CancelButtonIcon />;
  return (
    <FormProvider {...methods}>
      <Box sx={styles.container}>
        <CreateJobHeader handleGoBack={handleGoBack} />
        {createJobStepsMapper[step]}
        <CreateJobFooter
          handleGoBack={handleGoBack}
          BackButtonIcon={BackButtonIcon}
          backButtonLabel={step !== 0 ? t("back") : t("cancel")}
          handleGoToNextStep={handleGoToNextStep}
          nextButtonLabel={step === 3 ? t("publish") : t("next")}
          isSubmitting={isPending}
          handlePreviewClick={handlePreviewClick}
          jobId={jobId}
          showPreviewButton={step !== 3}
        />
      </Box>
    </FormProvider>
  );
};

export default CreateProfessionalJob;

const styles = {
  container: {
    display: "flex",
    flexDirection: "column",
    background: "#FFF",
    minHeight: "100vh",
  },
};
